import React, { FunctionComponent, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Autocomplete,
  FormControl,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { FileUpload as FileUploadIcon } from '@mui/icons-material';

import {
  penaltyWaiverPaymentStatusOptions,
  TPenaltyWaiverPaymentStatusOption,
} from '../../../../../../../constants';
import {
  defaultTextFieldProps,
  TPAutoCompleteArrowIconComponent,
  TPTextField,
} from '../../../../../../../components/Inputs';
import { capitalizeFirstLetter } from '../../../../../../../helper';
import DarkLoadingButton, {
  DarkButton,
  defaultButtonStyles,
} from '../../../../../../../components/V2/Buttons';
import { adminUpdatePenaltyWaiverTutorPaymentStatus } from '../../../../../../../store/V2/PenaltyWaiverSlice';

interface SelectedActionsProps {
  selectedList: string[];
  refreshList: () => void;
  onExport: () => void;
}

type TState = {
  selectedPaymentStatus: TPenaltyWaiverPaymentStatusOption;
};

const initialState: TState = {
  selectedPaymentStatus: penaltyWaiverPaymentStatusOptions[0],
};

const SelectedActions: FunctionComponent<SelectedActionsProps> = ({
  selectedList = [],
  refreshList,
  onExport,
}) => {
  const dispatch = useDispatch();

  // State
  const [state, setState] = useState({ ...initialState });
  const [isLoading, setLoading] = useState(false);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const handleSubmit = async () => {
    if (selectedList.length) {
      setLoading(true);

      const res: any = await dispatch(
        adminUpdatePenaltyWaiverTutorPaymentStatus({
          penaltyWaiverIds: selectedList,
          paymentStatus: state.selectedPaymentStatus.value,
        })
      );

      if (res && res.payload) {
        refreshList();
      }

      setLoading(false);
    }
  };

  return (
    <div>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12}>
          <Typography variant="h6" fontWeight={600}>
            Actions
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4} md={3} lg={2} xl={2}>
          <FormControl fullWidth>
            <Autocomplete
              size="small"
              disableClearable
              value={state.selectedPaymentStatus}
              options={penaltyWaiverPaymentStatusOptions}
              popupIcon={TPAutoCompleteArrowIconComponent}
              onChange={(even, value) =>
                updateStateAttributes({ selectedPaymentStatus: value })
              }
              isOptionEqualToValue={(option, value) =>
                option.label === value.value
              }
              getOptionLabel={(option) =>
                capitalizeFirstLetter(option.label.toLowerCase())
              }
              renderInput={(params) => (
                <TPTextField
                  {...defaultTextFieldProps}
                  {...params}
                  size="small"
                  label="Payment Status *"
                  placeholder="Select"
                />
              )}
            />
          </FormControl>
        </Grid>
        <Grid item>
          <Stack direction="row" alignItems="center" gap="10px">
            <DarkLoadingButton
              disableElevation
              variant="contained"
              color="inherit"
              loading={isLoading}
              onClick={handleSubmit}
              disabled={!state.selectedPaymentStatus}
              sx={{ ...defaultButtonStyles }}
            >
              Update
            </DarkLoadingButton>
            <DarkButton
              disableElevation
              variant="contained"
              color="inherit"
              onClick={onExport}
              startIcon={<FileUploadIcon />}
              sx={{ ...defaultButtonStyles }}
            >
              Export
            </DarkButton>
          </Stack>
        </Grid>
      </Grid>
    </div>
  );
};

export default SelectedActions;
